import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { mealCompositionService } from '@/services/mealComposition';
import { MEAL_COMPOSITIONS } from '@/constants/queryKeys';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';

interface CompositionHeaderActionsProps {
  composition: MealCompositionWithRecipes;
}

const CompositionHeaderActions: React.FC<CompositionHeaderActionsProps> = ({ composition }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useUser();

  // Only show actions if user owns the composition
  if (!user || composition.userId !== user.id) {
    return null;
  }

  const deleteMutation = useMutation({
    mutationFn: () => mealCompositionService.deleteMealComposition(composition.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEAL_COMPOSITIONS] });
      toast.success('Composition deleted successfully');
      navigate('/compositions');
    },
    onError: () => {
      toast.error('Failed to delete composition');
    },
  });

  const handleEdit = () => {
    navigate(`/composition/${composition.id}/edit`);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this composition? This action cannot be undone.')) {
      deleteMutation.mutate();
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={handleEdit}>
        <Edit className="h-4 w-4 mr-2" />
        Edit
      </Button>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleDelete}
        disabled={deleteMutation.isPending}
      >
        <Trash2 className="h-4 w-4 mr-2" />
        Delete
      </Button>
    </div>
  );
};

export default CompositionHeaderActions;